import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Pencil, Trash2, Link as LinkIcon, ChevronDown, ChevronUp,
  MoreVertical, Archive, Building2, ClipboardCheck, CalendarRange,
} from 'lucide-react';
import { driversApi, workItemsApi, projectsApi, tenantAdminApi } from '../api/services';
import type { AnnualStrategicDriver, WorkItem, Project, DriverLifecycleStatus } from '../types/models';
import HelpPanel from '../components/HelpPanel';
import { useAuth } from '../auth/AuthProvider';
import { usePrincipal } from '../context/PrincipalContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { useToast } from '@/components/ui/toast';

type DriverFilter = 'all' | 'mine' | 'organisational';

// ---------------------------------------------------------------------------
// StrategyPage — Annual Strategic Drivers.
//
// One card per driver: title + lifecycle chip + organisational toggle,
// description, progress slider, KPIs, linked items + projects, time-invested
// footer. A lifecycle menu (Draft / Active / Review / Archive…) hangs off
// each card via a Popover; archive triggers a confirmation dialog so the
// reason gets captured. The Add/Edit dialog is a thin form with a chip-style
// KPI list builder.
//
// Sliders use the native HTML range input rather than a Radix Slider shim —
// keeps the migration self-contained and the `accent-primary` Tailwind class
// handles the thumb/fill colour in both light and dark mode.
// ---------------------------------------------------------------------------

function lifecycleClasses(status?: string): { dot: string; chip: string } {
  switch (status) {
    case 'Active':
      return {
        dot: 'bg-emerald-500',
        chip: 'border-emerald-500/60 text-emerald-700 dark:text-emerald-400',
      };
    case 'Review':
      return {
        dot: 'bg-amber-500',
        chip: 'border-amber-500/60 text-amber-700 dark:text-amber-400',
      };
    case 'Draft':
      return {
        dot: 'bg-blue-500',
        chip: 'border-blue-500/60 text-blue-700 dark:text-blue-400',
      };
    default:
      return {
        dot: 'bg-muted-foreground/40',
        chip: 'border-border text-muted-foreground',
      };
  }
}

function ragClasses(rag: string): string {
  switch (rag) {
    case 'Green':
      return 'border-emerald-500/60 bg-emerald-500/15 text-emerald-700 dark:text-emerald-400';
    case 'Amber':
      return 'border-amber-500/60 bg-amber-500/15 text-amber-700 dark:text-amber-400';
    case 'Red':
      return 'border-rose-500/60 bg-rose-500/15 text-rose-700 dark:text-rose-400';
    default:
      return 'border-border bg-transparent text-muted-foreground';
  }
}

export default function StrategyPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const confirm = useConfirm();
  const { toast } = useToast();
  const { activePrincipal, isActingOnBehalf } = usePrincipal();
  const isAdmin = user?.isTenantAdmin || user?.role === 'Admin' || user?.role === 'TenantAdmin';
  // An EA acting on behalf of a TenantAdmin principal can also toggle the org
  // flag on drivers owned by that principal.
  const canToggleOrgFlag = (driver: AnnualStrategicDriver): boolean => {
    if (isAdmin) return true;
    if (isActingOnBehalf && activePrincipal?.isTenantAdmin && driver.userId === activePrincipal.id) return true;
    return false;
  };
  const [driverFilter, setDriverFilter] = useState<DriverFilter>('all');
  const [drivers, setDrivers] = useState<AnnualStrategicDriver[]>([]);
  const [loading, setLoading] = useState(true); // eslint-disable-line @typescript-eslint/no-unused-vars
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDriver, setEditDriver] = useState<Partial<AnnualStrategicDriver> | null>(null);
  const [kpiInput, setKpiInput] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [allWorkItems, setAllWorkItems] = useState<WorkItem[]>([]);
  const [allProjects, setAllProjects] = useState<Project[]>([]);
  const [showArchived, setShowArchived] = useState(false);
  const [archivedDrivers, setArchivedDrivers] = useState<AnnualStrategicDriver[]>([]);

  // Archive dialog
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [archiveDriverId, setArchiveDriverId] = useState<string | null>(null);
  const [archiveReason, setArchiveReason] = useState<string>('Completed');

  // Expanded descriptions
  const [expandedDescriptions, setExpandedDescriptions] = useState<Set<string>>(new Set());

  const toggleDescription = (id: string) => {
    setExpandedDescriptions(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Financial Year settings
  const [useFinancialYear, setUseFinancialYear] = useState(false);
  const [fyStartMonth, setFyStartMonth] = useState(1);
  const [fyLabel, setFyLabel] = useState('');
  const [timeInvested, setTimeInvested] = useState<Record<string, {
    self: { workItemMinutes: number; meetingMinutes: number };
    team: { workItemMinutes: number; meetingMinutes: number };
    byMember: {
      userId: string;
      name: string;
      isSelf: boolean;
      workItemMinutes: number;
      meetingMinutes: number;
    }[] | null;
  }>>({});
  // Which drivers have had their team breakdown expanded. Time invested shows
  // YOUR time by default — the team split is opt-in per driver, so the card
  // answers "what have I put in" at a glance without turning into a leaderboard.
  const [teamExpanded, setTeamExpanded] = useState<Record<string, boolean>>({});
  const [fyQuarter, setFyQuarter] = useState('');
  const [fyWeek, setFyWeek] = useState(0);

  // Don't offer "Show team" when it would expand to nothing. For admins that
  // means at least one contributor other than you; for everyone else it means
  // the aggregate is non-zero.
  const hasTeamTime = (t: { team: { workItemMinutes: number; meetingMinutes: number };
                            byMember: { isSelf: boolean }[] | null } | undefined) => {
    if (!t) return false;
    if (t.byMember) return t.byMember.some(m => !m.isSelf);
    return t.team.workItemMinutes > 0 || t.team.meetingMinutes > 0;
  };

  const loadDrivers = async () => {
    setLoading(true);
    try {
      setDrivers(await driversApi.getAll());
    } catch (err) {
      // silent-by-design: page load.
      console.error('Failed to load drivers', err);
    } finally {
      setLoading(false);
    }
  };

  const loadArchivedDrivers = async () => {
    try {
      setArchivedDrivers(await driversApi.getArchived());
    } catch (err) {
      // silent-by-design: supplementary "show archived" list.
      console.error('Failed to load archived drivers', err);
    }
  };

  const handleLifecycleChange = async (driverId: string, status: DriverLifecycleStatus, reason?: string) => {
    try {
      await driversApi.updateLifecycle(driverId, { status, reason });
      loadDrivers();
      if (showArchived) loadArchivedDrivers();
    } catch (err: any) {
      console.error('Failed to update lifecycle', err);
      toast({ variant: 'error', message: err?.response?.data?.error ?? 'Failed to update driver lifecycle' });
    }
  };

  const handleArchiveConfirm = async () => {
    if (!archiveDriverId) return;
    await handleLifecycleChange(archiveDriverId, 'Archived', archiveReason);
    setArchiveDialogOpen(false);
    setArchiveDriverId(null);
    setArchiveReason('Completed');
  };

  const openArchiveDialog = (driverId: string) => {
    setArchiveDriverId(driverId);
    setArchiveDialogOpen(true);
  };

  useEffect(() => { loadDrivers(); }, []);

  useEffect(() => { if (showArchived) loadArchivedDrivers(); }, [showArchived]);

  // Load YTD time invested once drivers are in, and whenever the set changes.
  useEffect(() => {
    if (drivers.length === 0) return;
    driversApi.getTimeInvested()
      .then(r => setTimeInvested(r.timeInvested))
      .catch(() => {});
  }, [drivers]);

  // Load work items for linked count and alignment
  useEffect(() => {
    workItemsApi.getAll().then(setAllWorkItems).catch(() => {});
  }, []);

  // Load projects for linked display under drivers
  useEffect(() => {
    projectsApi.getAll().then(setAllProjects).catch(() => {});
  }, []);

  // Load FY settings on mount
  useEffect(() => {
    tenantAdminApi.getFinancialYear().then((fy: any) => {
      setUseFinancialYear(fy.useFinancialYear);
      setFyStartMonth(fy.useFinancialYear ? fy.financialYearStartMonth : 1);
      setFyLabel(fy.currentFYLabel);
      setFyQuarter(`Q${fy.currentQuarter}`);
      setFyWeek(fy.currentFYWeek);
    }).catch(() => {});
  }, []);

  // Format a minute count as "Xh Ym" for the time-invested footer.
  const formatMinutes = (m: number) => {
    const h = Math.floor(m / 60);
    const min = Math.round(m % 60);
    return `${h}h ${min}m`;
  };

  // Year-filtered drivers (exclude archived unless toggled), respecting org filter
  const filteredDrivers = useMemo(
    () => drivers
      .filter(d => d.year === selectedYear && d.lifecycleStatus !== 'Archived')
      .filter(d => {
        if (driverFilter === 'mine') return d.userId === user?.id;
        if (driverFilter === 'organisational') return d.isOrganisational;
        return true; // 'all'
      })
      .sort((a, b) => a.displayOrder - b.displayOrder),
    [drivers, selectedYear, driverFilter, user?.id]
  );

  // Archived filtered by year
  const filteredArchivedDrivers = useMemo(
    () => archivedDrivers.filter(d => d.year === selectedYear),
    [archivedDrivers, selectedYear]
  );

  // Linked work items count per driver
  const linkedCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    allWorkItems.forEach(wi => {
      if (wi.strategicDriverId) counts[wi.strategicDriverId] = (counts[wi.strategicDriverId] || 0) + 1;
    });
    return counts;
  }, [allWorkItems]);

  // Auto-progress: calculate from linked work items completion
  const autoProgress = useMemo(() => {
    const map: Record<string, { percent: number; total: number; done: number }> = {};
    drivers.forEach(d => {
      const linked = allWorkItems.filter(w => w.strategicDriverId === d.id);
      const done = linked.filter(w => w.status === 'Done').length;
      map[d.id] = {
        percent: linked.length > 0 ? Math.round((done / linked.length) * 100) : 0,
        total: linked.length,
        done,
      };
    });
    return map;
  }, [drivers, allWorkItems]);

  // Projects grouped by strategic driver
  const projectsByDriver = useMemo(() => {
    const map: Record<string, Project[]> = {};
    allProjects.forEach(p => {
      if (p.strategicDriverId) {
        if (!map[p.strategicDriverId]) map[p.strategicDriverId] = [];
        map[p.strategicDriverId].push(p);
      }
    });
    return map;
  }, [allProjects]);

  // Alignment summary: % of this week's items linked to a strategic driver
  const alignmentPercent = useMemo(() => {
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay());
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 7);
    const startStr = startOfWeek.toISOString().split('T')[0];
    const endStr = endOfWeek.toISOString().split('T')[0];
    const weekItems = allWorkItems.filter(wi => wi.targetDate >= startStr && wi.targetDate <= endStr);
    if (weekItems.length === 0) return null;
    const aligned = weekItems.filter(wi => wi.strategicDriverId).length;
    return Math.round((aligned / weekItems.length) * 100);
  }, [allWorkItems]);

  // Available years from data
  const availableYears = useMemo(() => {
    const years = new Set(drivers.map(d => d.year));
    years.add(new Date().getFullYear());
    return Array.from(years).sort((a, b) => b - a);
  }, [drivers]);

  // FY label for a given year
  const getYearLabel = (year: number): string => {
    if (!useFinancialYear || fyStartMonth === 1) return `${year}`;
    const endYear = year + 1;
    return `FY${year % 100}/${endYear % 100}`;
  };

  // Inline progress update
  const handleProgressUpdate = async (id: string, value: number) => {
    try {
      await driversApi.update(id, { progressPercentage: value });
      setDrivers(prev => prev.map(d => d.id === id ? { ...d, progressPercentage: value } : d));
    } catch (err: any) {
      console.error('Failed to update progress', err);
      toast({ variant: 'error', message: err?.response?.data?.error ?? 'Failed to update driver progress' });
    }
  };

  const handleOrganisationalToggle = async (driver: AnnualStrategicDriver) => {
    const next = !driver.isOrganisational;
    try {
      await driversApi.update(driver.id, { isOrganisational: next });
      setDrivers(prev => prev.map(d => d.id === driver.id ? { ...d, isOrganisational: next } : d));
    } catch (err: any) {
      console.error('Failed to toggle organisational flag', err);
      toast({ variant: 'error', message: err?.response?.data?.error ?? 'Failed to update organisational flag' });
    }
  };

  const handleSave = async () => {
    if (!editDriver?.title) return;
    try {
      if (editDriver.id) {
        await driversApi.update(editDriver.id, editDriver);
      } else {
        await driversApi.create({ ...editDriver, year: editDriver.year || new Date().getFullYear() });
      }
      setDialogOpen(false);
      setEditDriver(null);
      loadDrivers();
    } catch (err: any) {
      console.error('Failed to save driver', err);
      toast({ variant: 'error', message: err?.response?.data?.error ?? 'Failed to save driver' });
    }
  };

  const handleDelete = async (id: string) => {
    const ok = await confirm({
      title: 'Delete strategic driver?',
      body: 'The driver will be removed from your annual strategy. Work items and projects linked to it stay but lose the connection.',
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    try {
      await driversApi.delete(id);
      loadDrivers();
    } catch (err: any) {
      console.error('Failed to delete', err);
      toast({ variant: 'error', message: err?.response?.data?.error ?? 'Failed to delete driver' });
    }
  };

  const addKpi = () => {
    if (!kpiInput.trim()) return;
    setEditDriver(prev => ({
      ...prev,
      kpis: [...(prev?.kpis || []), kpiInput.trim()],
    }));
    setKpiInput('');
  };

  const DRIVER_FILTERS: { value: DriverFilter; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'mine', label: 'My Drivers' },
    { value: 'organisational', label: 'Organisational' },
  ];

  return (
    <div className="mx-auto max-w-[1280px] p-4 md:p-6">
      {/* Header */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-serif text-2xl font-light tracking-tight">Annual Strategic Drivers</h1>
          <p className="text-sm text-muted-foreground">Define the 3-4 strategic themes that guide your year</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={String(selectedYear)}
            onValueChange={(v) => setSelectedYear(Number(v))}
          >
            <SelectTrigger className="w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {availableYears.map(y => (
                <SelectItem key={y} value={String(y)}>{getYearLabel(y)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {useFinancialYear && fyLabel && (
            <p className="whitespace-nowrap text-sm text-muted-foreground">
              {fyQuarter} · Week {fyWeek}
            </p>
          )}
          <div
            role="radiogroup"
            aria-label="Driver filter"
            className="inline-flex rounded-md border border-border bg-card/40 p-0.5"
            onKeyDown={(e) => {
              const order = DRIVER_FILTERS.map(f => f.value);
              const i = order.indexOf(driverFilter);
              if (i < 0) return;
              if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                e.preventDefault();
                setDriverFilter(order[(i - 1 + order.length) % order.length]);
              } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                e.preventDefault();
                setDriverFilter(order[(i + 1) % order.length]);
              }
            }}
          >
            {DRIVER_FILTERS.map(f => {
              const checked = driverFilter === f.value;
              return (
                <Button
                  key={f.value}
                  role="radio"
                  aria-checked={checked}
                  tabIndex={checked ? 0 : -1}
                  size="sm"
                  variant={checked ? 'default' : 'ghost'}
                  onClick={() => setDriverFilter(f.value)}
                  className="h-7 px-3 text-xs"
                >
                  {f.label}
                </Button>
              );
            })}
          </div>
          <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
            <Checkbox
              checked={showArchived}
              onCheckedChange={(c) => setShowArchived(!!c)}
              aria-label="Show archived"
            />
            Show archived
          </label>
          <Button
            className="gap-1.5"
            onClick={() => {
              setEditDriver({
                title: '', description: '',
                displayOrder: drivers.length + 1, isActive: true,
                kpis: [], year: selectedYear, progressPercentage: 0,
              });
              setDialogOpen(true);
            }}
          >
            <Plus className="h-4 w-4" />
            Add Driver
          </Button>
        </div>
      </div>

      <HelpPanel title="About Strategic Drivers" storageKey="strategy">
        Strategic drivers define your annual priorities. Link projects and KPIs to track progress.
      </HelpPanel>

      {/* Alignment summary */}
      {alignmentPercent !== null && (
        <Card
          className={cn(
            'mb-5 border-l-[3px]',
            alignmentPercent >= 60
              ? 'border-l-emerald-500'
              : alignmentPercent >= 30
                ? 'border-l-amber-500'
                : 'border-l-rose-500',
          )}
        >
          <CardContent className="py-3">
            <div className="flex items-center gap-3">
              <LinkIcon
                className={cn(
                  'h-4 w-4',
                  alignmentPercent >= 60
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : 'text-amber-600 dark:text-amber-400',
                )}
              />
              <p className="text-sm">
                <strong>{alignmentPercent}%</strong> of this week's work items are aligned to a strategic driver
              </p>
              <div className="ml-auto h-1.5 max-w-[200px] flex-1 overflow-hidden rounded-full bg-muted">
                <div
                  className={cn(
                    'h-full transition-all',
                    alignmentPercent >= 60
                      ? 'bg-emerald-500'
                      : alignmentPercent >= 30
                        ? 'bg-amber-500'
                        : 'bg-rose-500',
                  )}
                  style={{ width: `${alignmentPercent}%` }}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Driver grid */}
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        {filteredDrivers.map((driver) => {
          const lc = lifecycleClasses(driver.lifecycleStatus);
          const isOwner = driver.userId === user?.id;
          const canEditOrg = canToggleOrgFlag(driver);
          const expanded = expandedDescriptions.has(driver.id);
          const linkedCount = linkedCounts[driver.id] || 0;
          const auto = autoProgress[driver.id];

          // Left edge bar tone: primary for Active, amber for Review, neutral
          // for Draft / other. Draft cards are also slightly faded.
          const cardBar =
            driver.lifecycleStatus === 'Active'
              ? 'border-l-primary'
              : driver.lifecycleStatus === 'Review'
                ? 'border-l-amber-500'
                : 'border-l-border';

          return (
            <Card
              key={driver.id}
              className={cn(
                'h-full border-l-[3px]',
                cardBar,
                driver.lifecycleStatus === 'Draft' && 'opacity-70',
              )}
            >
              <CardContent>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-base font-semibold">{driver.title}</p>
                      <span
                        className={cn(
                          'inline-flex items-center gap-1 rounded-full border bg-transparent px-2 py-0.5 text-[11px] font-medium',
                          lc.chip,
                        )}
                      >
                        <span aria-hidden className={cn('h-1.5 w-1.5 rounded-full', lc.dot)} />
                        {driver.lifecycleStatus || 'Active'}
                      </span>
                      {(driver.isOrganisational || canEditOrg) && (
                        <button
                          type="button"
                          onClick={canEditOrg ? () => handleOrganisationalToggle(driver) : undefined}
                          disabled={!canEditOrg}
                          className={cn(
                            'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors',
                            driver.isOrganisational
                              ? 'border-blue-500/60 bg-blue-500/15 text-blue-700 dark:text-blue-300'
                              : 'border-border text-muted-foreground hover:border-foreground/40 hover:text-foreground',
                            !canEditOrg && 'cursor-default',
                          )}
                          aria-pressed={driver.isOrganisational}
                        >
                          <Building2 className="h-3 w-3" />
                          {driver.isOrganisational ? 'Org-wide' : 'Make org-wide'}
                        </button>
                      )}
                    </div>
                    {driver.isOrganisational && driver.userId !== user?.id && driver.ownerName && (
                      <p className="mt-1 text-xs text-muted-foreground">Owner: {driver.ownerName}</p>
                    )}
                    {driver.description && (
                      <div className="mt-1">
                        <button
                          type="button"
                          onClick={() => toggleDescription(driver.id)}
                          className="flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground"
                          aria-expanded={expanded}
                        >
                          Description
                          {expanded
                            ? <ChevronUp className="h-3.5 w-3.5" />
                            : <ChevronDown className="h-3.5 w-3.5" />}
                        </button>
                        <div
                          className={cn(
                            'overflow-hidden transition-[max-height] duration-200 ease-out',
                            expanded ? 'max-h-[400px]' : 'max-h-0',
                          )}
                        >
                          <p className="mt-1 text-sm text-muted-foreground">{driver.description}</p>
                        </div>
                      </div>
                    )}
                  </div>
                  {isOwner && (
                    <div className="flex items-center">
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button size="icon-sm" variant="ghost" aria-label="Lifecycle status">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent align="end" className="w-44 p-1">
                          {(['Draft', 'Active', 'Review'] as DriverLifecycleStatus[]).map((status) => {
                            const sc = lifecycleClasses(status);
                            return (
                              <button
                                key={status}
                                type="button"
                                onClick={() => handleLifecycleChange(driver.id, status)}
                                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted"
                              >
                                <span aria-hidden className={cn('h-2 w-2 rounded-full', sc.dot)} />
                                Set as {status}
                              </button>
                            );
                          })}
                          <hr className="my-1 border-border" />
                          <button
                            type="button"
                            onClick={() => openArchiveDialog(driver.id)}
                            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm text-muted-foreground hover:bg-muted"
                          >
                            <Archive className="h-4 w-4" />
                            Archive…
                          </button>
                        </PopoverContent>
                      </Popover>
                      <Button
                        size="icon-sm"
                        variant="ghost"
                        onClick={() => { setEditDriver(driver); setDialogOpen(true); }}
                        aria-label="Edit driver"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon-sm"
                        variant="ghost"
                        onClick={() => handleDelete(driver.id)}
                        aria-label="Delete driver"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>

                {/* Progress — auto-derived from linked work items.
                    The redesign deliberately drops the 0% manual-override
                    noise: drivers with no linked items render as
                    "Not started this year — No items linked" rather than
                    a 0% bar. Owners can still nudge a manual override via
                    the slider once at least one item is linked. */}
                <div className="mb-1 mt-3">
                  {auto && auto.total === 0 ? (
                    <div className="rounded-md bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                      <p className="font-medium">Not started this year</p>
                      <p className="mt-0.5 text-[11px] text-muted-foreground/80">
                        No items linked. Tag a work item with this driver to start tracking progress.
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="mb-1 flex items-center justify-between">
                        <p className="text-xs text-muted-foreground">Annual progress</p>
                        <p className="text-xs font-semibold tabular-nums">
                          {/* Backend-computed autoProgress is the source of truth — it
                              uses Math.Round and returns null on empty (vs the local
                              useMemo's Math.round + 0-on-empty) so the gauge on Dashboard
                              and this chip agree exactly. Local `auto` still feeds the
                              item-count breakdown below. */}
                          {driver.autoProgress ?? auto?.percent ?? driver.progressPercentage}%
                        </p>
                      </div>
                      <input
                        type="range"
                        min={0}
                        max={100}
                        value={driver.progressPercentage}
                        onChange={(e) => {
                          if (!isOwner) return;
                          const v = Number(e.target.value);
                          setDrivers(prev => prev.map(d => d.id === driver.id ? { ...d, progressPercentage: v } : d));
                        }}
                        onMouseUp={(e) => {
                          if (!isOwner) return;
                          handleProgressUpdate(driver.id, Number((e.target as HTMLInputElement).value));
                        }}
                        onTouchEnd={(e) => {
                          if (!isOwner) return;
                          handleProgressUpdate(driver.id, Number((e.target as HTMLInputElement).value));
                        }}
                        disabled={!isOwner}
                        aria-label={`Progress for ${driver.title}`}
                        className="h-2 w-full cursor-pointer appearance-none rounded-full bg-muted accent-primary disabled:cursor-not-allowed disabled:opacity-60"
                      />
                      {auto && (
                        <p className="mt-1 text-[11px] text-muted-foreground">
                          {auto.total} item{auto.total === 1 ? '' : 's'}, {auto.done} done
                          {driver.progressPercentage !== auto.percent && ` · manual override at ${driver.progressPercentage}%`}
                        </p>
                      )}
                    </>
                  )}
                </div>

                {/* Structured / plain KPIs */}
                <div className="mt-3">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">KPIs</p>
                  {driver.structuredKpis && driver.structuredKpis.length > 0 ? (
                    driver.structuredKpis.map((kpi: any, i: number) => {
                      const onTrack = kpi.direction === 'HigherIsBetter'
                        ? (kpi.currentValue ?? 0) >= (kpi.targetValue ?? 0)
                        : (kpi.currentValue ?? 0) <= (kpi.targetValue ?? 0);
                      return (
                        <div key={i} className="flex items-center gap-2 py-0.5 pl-2">
                          <span aria-hidden className={cn('h-2 w-2 rounded-full', onTrack ? 'bg-emerald-500' : 'bg-rose-500')} />
                          <p className="min-w-0 flex-1 truncate text-sm text-muted-foreground">{kpi.name}</p>
                          <p className="whitespace-nowrap text-xs text-muted-foreground tabular-nums">
                            {kpi.currentValue ?? '—'}{kpi.unit ? ` ${kpi.unit}` : ''} / {kpi.targetValue ?? '—'}{kpi.unit ? ` ${kpi.unit}` : ''}
                          </p>
                        </div>
                      );
                    })
                  ) : driver.kpis && driver.kpis.length > 0 ? (
                    driver.kpis.map((kpi: string, i: number) => (
                      <p key={i} className="pl-2 text-sm text-muted-foreground">• {kpi}</p>
                    ))
                  ) : (
                    <p className="mt-1 pl-2 text-sm italic text-muted-foreground/60">
                      No KPIs defined — add KPIs to track progress
                    </p>
                  )}
                </div>

                {/* Meta chips: display order, year, linked count */}
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className="inline-flex items-center rounded-full border border-border bg-transparent px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                    #{driver.displayOrder}
                  </span>
                  <span className="inline-flex items-center rounded-full border border-border bg-transparent px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                    {getYearLabel(driver.year)}
                  </span>
                  <span
                    className={cn(
                      'inline-flex items-center gap-1 rounded-full border bg-transparent px-2 py-0.5 text-[11px] font-medium',
                      linkedCount > 0
                        ? 'border-blue-500/60 text-blue-700 dark:text-blue-300'
                        : 'border-border text-muted-foreground',
                    )}
                  >
                    <LinkIcon className="h-3 w-3" />
                    {linkedCount} item{linkedCount !== 1 ? 's' : ''} linked
                  </span>
                </div>

                {/* Linked projects */}
                <div className="mt-4">
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Projects</p>
                  {projectsByDriver[driver.id] && projectsByDriver[driver.id].length > 0 ? (
                    <div className="flex flex-col gap-1">
                      {projectsByDriver[driver.id].map(project => (
                        <button
                          key={project.id}
                          type="button"
                          onClick={() => navigate(`/projects/${project.id}`)}
                          className="flex items-center gap-2 rounded-md px-2 py-1 text-left transition-colors hover:bg-muted/40"
                        >
                          <p className="min-w-0 flex-1 truncate text-sm font-medium">{project.title}</p>
                          <span
                            className={cn(
                              'inline-flex min-w-[50px] items-center justify-center rounded-full border px-2 py-0.5 text-[10px] font-semibold',
                              ragClasses(project.ragStatus),
                            )}
                          >
                            {project.ragStatus}
                          </span>
                          <span className="min-w-[32px] text-right text-[11px] text-muted-foreground tabular-nums">
                            {project.progressPercentage}%
                          </span>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="pl-2 text-sm italic text-muted-foreground/60">No projects linked</p>
                  )}
                </div>

                {/* Time invested */}
                {timeInvested[driver.id] && (
                  <div className="mt-4 border-t border-border pt-3">
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Time invested ({fyLabel || 'YTD'})
                    </p>
                    <div className="flex flex-col gap-1">
                      {driver.isOrganisational ? (
                        <>
                          <TimeInvestedRow label="Self" data={timeInvested[driver.id].self} formatMinutes={formatMinutes} />

                          {/* Team is opt-in. Expanded, tenant admins get the
                              named per-contributor split — it answers "who is
                              actually carrying this priority" — while everyone
                              else gets the anonymous aggregate. byMember is
                              null when not permitted, [] when nobody has
                              logged time. */}
                          {hasTeamTime(timeInvested[driver.id]) && (
                            <>
                              <button
                                type="button"
                                onClick={() => setTeamExpanded(prev => ({ ...prev, [driver.id]: !prev[driver.id] }))}
                                aria-expanded={!!teamExpanded[driver.id]}
                                className="mt-1 flex items-center gap-1 self-start text-xs font-semibold text-muted-foreground hover:text-foreground"
                              >
                                {teamExpanded[driver.id]
                                  ? <ChevronUp className="h-3.5 w-3.5" />
                                  : <ChevronDown className="h-3.5 w-3.5" />}
                                {teamExpanded[driver.id] ? 'Hide team' : 'Show team'}
                              </button>

                              {teamExpanded[driver.id] && (
                                timeInvested[driver.id].byMember
                                  ? timeInvested[driver.id].byMember!
                                      .filter(m => !m.isSelf)
                                      .map(m => (
                                        <TimeInvestedRow
                                          key={m.userId}
                                          label={m.name}
                                          data={m}
                                          formatMinutes={formatMinutes}
                                        />
                                      ))
                                  : <TimeInvestedRow label="Team" data={timeInvested[driver.id].team} formatMinutes={formatMinutes} />
                              )}
                            </>
                          )}
                        </>
                      ) : (
                        <TimeInvestedRow label={null} data={timeInvested[driver.id].self} formatMinutes={formatMinutes} />
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Archive reason dialog */}
      <Dialog open={archiveDialogOpen} onOpenChange={(o) => !o && setArchiveDialogOpen(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Archive Strategic Driver</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">Why is this driver being archived?</p>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="archive-reason">Reason</Label>
            <Select value={archiveReason} onValueChange={setArchiveReason}>
              <SelectTrigger id="archive-reason"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Completed">Completed — objectives achieved</SelectItem>
                <SelectItem value="Superseded">Superseded — replaced by another driver</SelectItem>
                <SelectItem value="Abandoned">Abandoned — no longer relevant</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setArchiveDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleArchiveConfirm}
              className="gap-1.5 bg-amber-600 text-white hover:bg-amber-700"
            >
              <Archive className="h-4 w-4" />
              Archive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archived drivers section */}
      {showArchived && filteredArchivedDrivers.length > 0 && (
        <div className="mt-6">
          <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold text-muted-foreground">
            <Archive className="h-5 w-5" />
            Archived Drivers ({filteredArchivedDrivers.length})
          </h2>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            {filteredArchivedDrivers.map((driver) => (
              <Card key={driver.id} className="h-full border-l-[3px] border-l-border opacity-60">
                <CardContent>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-base font-semibold">{driver.title}</p>
                      <div className="mt-1 flex flex-wrap gap-2">
                        <span className="inline-flex items-center rounded-full border border-border bg-transparent px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                          Archived
                        </span>
                        {driver.archiveReason && (
                          <span className="inline-flex items-center rounded-full border border-border bg-transparent px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                            {driver.archiveReason}
                          </span>
                        )}
                      </div>
                      {driver.archivedAt && (
                        <p className="mt-1 text-xs text-muted-foreground">
                          Archived {new Date(driver.archivedAt).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => handleLifecycleChange(driver.id, 'Active')}>
                      Restore
                    </Button>
                  </div>
                  <div className="mt-3">
                    <div className="mb-1 flex justify-between">
                      <p className="text-xs text-muted-foreground">Final Progress</p>
                      <p className="text-xs font-semibold tabular-nums">{driver.progressPercentage}%</p>
                    </div>
                    <div className="h-1 w-full overflow-hidden rounded-full bg-muted">
                      <div className="h-full bg-muted-foreground/40" style={{ width: `${driver.progressPercentage}%` }} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Add / edit driver dialog */}
      <Dialog open={dialogOpen} onOpenChange={(o) => !o && setDialogOpen(false)}>
        <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editDriver?.id ? 'Edit Strategic Driver' : 'New Strategic Driver'}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="drv-title">Title</Label>
              <Input
                id="drv-title"
                value={editDriver?.title || ''}
                onChange={(e) => setEditDriver(prev => ({ ...prev, title: e.target.value }))}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="drv-desc">Description</Label>
              <Textarea
                id="drv-desc"
                rows={3}
                value={editDriver?.description || ''}
                onChange={(e) => setEditDriver(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="drv-order">Display Order</Label>
                <Input
                  id="drv-order"
                  type="number"
                  value={editDriver?.displayOrder || 1}
                  onChange={(e) => setEditDriver(prev => ({ ...prev, displayOrder: parseInt(e.target.value) || 1 }))}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="drv-year">Year</Label>
                <Input
                  id="drv-year"
                  type="number"
                  value={editDriver?.year || new Date().getFullYear()}
                  onChange={(e) => setEditDriver(prev => ({ ...prev, year: parseInt(e.target.value) }))}
                />
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="drv-progress">Progress (%)</Label>
              <Input
                id="drv-progress"
                type="number"
                min={0}
                max={100}
                value={editDriver?.progressPercentage || 0}
                onChange={(e) => setEditDriver(prev => ({ ...prev, progressPercentage: parseFloat(e.target.value) || 0 }))}
              />
            </div>
            <div>
              <Label htmlFor="drv-kpi">KPIs</Label>
              <div className="mt-1 flex items-center gap-2">
                <Input
                  id="drv-kpi"
                  placeholder="Add a KPI"
                  value={kpiInput}
                  onChange={(e) => setKpiInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addKpi(); } }}
                />
                <Button type="button" variant="outline" onClick={addKpi}>Add</Button>
              </div>
              {editDriver?.kpis && editDriver.kpis.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {editDriver.kpis.map((kpi, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/30 px-2 py-0.5 text-[11px] font-medium"
                    >
                      {kpi}
                      <button
                        type="button"
                        onClick={() => setEditDriver(prev => ({
                          ...prev,
                          kpis: prev?.kpis?.filter((_, idx) => idx !== i),
                        }))}
                        aria-label={`Remove ${kpi}`}
                        className="rounded-full p-0.5 hover:bg-foreground/10"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="drv-notes">Notes</Label>
              <Textarea
                id="drv-notes"
                rows={2}
                value={editDriver?.notes || ''}
                onChange={(e) => setEditDriver(prev => ({ ...prev, notes: e.target.value }))}
              />
            </div>
            {/* Organisational toggle lives on the driver card itself so it's
                one click away and visible to EAs of TenantAdmins, not just
                admins. */}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// TimeInvestedRow — small row in the per-driver "Time invested" footer.
// Shows two icon + minute-count pairs: work item time (ClipboardCheck) and
// meeting time (CalendarRange). When the driver isn't organisational, the row
// has no label. When it is, the label is "Self" plus either "Team" (the
// anonymous aggregate) or one row per named contributor for tenant admins.
// ---------------------------------------------------------------------------
interface TimeInvestedRowProps {
  label: string | null;
  data: { workItemMinutes: number; meetingMinutes: number };
  formatMinutes: (m: number) => string;
}

function TimeInvestedRow({ label, data, formatMinutes }: TimeInvestedRowProps) {
  return (
    <div className="flex items-center gap-4">
      {label !== null && (
        <p className="min-w-[40px] max-w-[120px] truncate text-xs font-semibold text-muted-foreground" title={label}>{label}</p>
      )}
      <div className="flex items-center gap-1">
        <ClipboardCheck className="h-3.5 w-3.5 text-muted-foreground" />
        <p className="text-xs text-muted-foreground">{formatMinutes(data.workItemMinutes)}</p>
      </div>
      <div className="flex items-center gap-1">
        <CalendarRange className="h-3.5 w-3.5 text-muted-foreground" />
        <p className="text-xs text-muted-foreground">{formatMinutes(data.meetingMinutes)}</p>
      </div>
    </div>
  );
}
